import { Duration } from 'moment';
import useSound from 'use-sound';
import { styled } from 'styled-components';

import Button from '@src/components/styled/Button';
import _startGame from '@src/io/events_out/_startGame';
import _drawCard from '@src/io/events_out/_drawCard';
import _endTurn from '@src/io/events_out/_endTurn';
import { setStage } from '@src/state/slices/slice_app';
import { Stage } from '@src/state/slices/types';
import { clearCurrentGame } from '@src/state/slices/slice_game';
import { _useDispatch } from '@src/state/hooks';
import drawCardSound from '@src/asset/sound/drawCard.wav';

import _unwatchGame from '@src/io/events_out/_unwatchGame';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  text-align: center;
  font-size: 32pt;

  margin-top: 1rem;

  padding-top: 1rem;
  padding-bottom: 1rem;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 1rem;
`;

const StackedRow = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

type Props = {
  gameId: string;
  gameStarted: boolean;
  canStart: boolean;
  isPlayersTurn: boolean;
  gameTerminated: boolean;
  gameEnded: boolean;
  gameWinner: string | null;
  untilStart: Duration | null;
  gameWager: number;
  watchingGame: boolean;
};

export default function ActionButtons(props: Props) {
  const dispatch = _useDispatch();

  const [playDrawCardSound] = useSound(drawCardSound, { volume: 0.25 });

  const startGame = () => {
    if (!props.canStart) return;
    _startGame(props.gameId);
  };

  const drawCard = () => {
    if (!props.isPlayersTurn) return;
    playDrawCardSound();
    _drawCard(props.gameId);
  };

  const endTurn = () => {
    if (!props.isPlayersTurn) return;
    _endTurn(props.gameId);
  };

  const leaveGame = () => {
    if (!props.gameTerminated && !props.watchingGame) return;
    dispatch(setStage({ stage: Stage.LOBBY, stageMeta: {} }));
    dispatch(clearCurrentGame());
    _unwatchGame(props.gameId);
  };

  if (props.watchingGame) {
    return (
      <Container>
        <Row>
          <Button onClick={leaveGame}>RETURN TO LOBBY</Button>
        </Row>
      </Container>
    );
  }

  if (props.gameTerminated) {
    return (
      <Container style={{ backgroundColor: 'black' }}>
        {getGameOverMsg(props)}
        <Row>
          <Button onClick={leaveGame}>LEAVE GAME</Button>
        </Row>
      </Container>
    );
  }

  if (!props.gameStarted && !props.canStart) {
    return <Container>Waiting for game to start...</Container>;
  }

  if (!props.gameStarted && props.canStart) {
    return (
      <Container>
        <Row>
          <Button onClick={startGame}>START GAME</Button>
        </Row>
      </Container>
    );
  }

  // game must have started
  return (
    <Container>
      <Row>
        <Button onClick={drawCard} disabled={!props.isPlayersTurn}>
          DRAW CARD
        </Button>
        <Button onClick={endTurn} disabled={!props.isPlayersTurn}>
          END TURN
        </Button>
      </Row>
    </Container>
  );
}

function getGameOverMsg(props: Props) {
  const wagerText =
    props.gameWager > 0 ? ` GOT ${props.gameWager} $GOLD` : null;
  return props.gameEnded && props.gameWinner
    ? `GAME OVER - WINNER: ${props.gameWinner}${wagerText ?? ''}`
    : 'Game terminated...';
}
